import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import type { SetParams } from "@/features/opportunities/components/sets/SetParamsPopover/SetParamsPopover.js";

import type { PersonaOpportunitySet } from "../types/persona-opportunities.types.js";

export interface HubSetParamsPopoverModel {
  params: SetParams;
  personas: string[];
  industryLabel: string | null;
  benchmarkIndustryLabel: string | null;
  isGenerating: boolean;
}

/** Preserve first-seen order; drop blanks and repeated titles (many personas can share one title). */
export function uniquePersonaTitlesForPopover(personas: readonly string[]): string[] {
  const seen = new Set<string>();
  const titles: string[] = [];
  for (const raw of personas) {
    const title = raw.trim();
    if (!title || seen.has(title)) continue;
    seen.add(title);
    titles.push(title);
  }
  return titles;
}

function catalogLabel(
  value: string | null | undefined,
  labels: ReadonlyMap<string, string> | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return labels?.get(trimmed) ?? humanizeBrandName(trimmed);
}

/**
 * Hub cards show a subset of the old Set Parameters popover.
 * Competitor cards keep the nine creation fields. Persona cards show personas
 * and ad platform instead of the competitor fields. Empty values stay off the model
 * so the popover hides them.
 */
export function toHubSetParamsPopover(
  set: PersonaOpportunitySet,
  industryLabelByIndex?: ReadonlyMap<string, string>,
): HubSetParamsPopoverModel {
  const isCompetitor = set.type === "COMPETITOR";
  const isPersona = set.type === "PERSONA";
  const personas = isPersona ? uniquePersonaTitlesForPopover(set.personas ?? []) : [];

  return {
    personas,
    industryLabel: catalogLabel(set.industry, industryLabelByIndex),
    benchmarkIndustryLabel: isCompetitor
      ? catalogLabel(set.benchmarkIndustry, industryLabelByIndex)
      : null,
    isGenerating: set.status === "GENERATING",
    params: {
      industryCompetitors: isCompetitor ? set.industryCompetitors : null,
      myBrand: isCompetitor ? set.myBrand ?? null : null,
      mainCompetitor: isCompetitor ? set.mainCompetitor ?? null : null,
      products: isCompetitor ? set.products ?? [] : null,
      adPlatforms: isPersona && set.adPlatform ? [set.adPlatform] : null,
      minimalGeneration: false,
      strategicPriorities: null,
      brandVoice: null,
      startDate: set.startDate,
      endDate: set.endDate,
      generationStartedAt: set.generationStartedAt ?? null,
      generationCompletedAt: set.generationCompletedAt ?? null,
      createdAt: set.createdAt,
    },
  };
}
